// src/pages/SearchListing.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import toast from "react-hot-toast";

import Header from "../components/Header";
import Footer from "../components/Footer";
import axiosInstance from "../../axiosInstance";

// --- helpers ---
const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
const currency = (n) =>
  typeof n === "number"
    ? n.toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 })
    : null;

const SORTS = [
  { key: "relevance", label: "Relevance" },           // backend default (score desc)
  { key: "rating_desc", label: "Rating (High → Low)" },
  { key: "rating_asc", label: "Rating (Low → High)" },
  // { key: "price_asc", label: "Price (Low → High)" },
  // { key: "price_desc", label: "Price (High → Low)" },
  { key: "recent", label: "Recent" },                 // updatedAt desc
];

// highlight matched tokens in a string
const highlight = (text, tokens) => {
  if (!text) return "";
  if (!tokens?.length) return text;
  const escaped = tokens.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const re = new RegExp(`(${escaped.join("|")})`, "ig");
  return text.split(re).map((part, i) =>
    re.test(part) ? (
      <mark key={i} style={{ padding: 0, background: "transparent", color: "#0d6efd", fontWeight: 700 }}>
        {part}
      </mark>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    )
  );
};

const SearchVendorListing = () => {
  const navigate = useNavigate();
  const routerLocation = useLocation(); // ✅ use this instead of global `location`
  const { term } = useParams();

  // UI state
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(12);
  const [total, setTotal] = useState(0);
  const [sort, setSort] = useState("relevance");

  // tokens for highlight
  const tokens = useMemo(() => {
    return String(term || "")
      .toLowerCase()
      .split(/[^a-z0-9]+/i)
      .map((t) => t.trim())
      .filter((t) => t.length > 1);
  }, [term]);

  // sync page/limit/sort from querystring
  useEffect(() => {
    const qs = new URLSearchParams(routerLocation.search);
    const qPage = parseInt(qs.get("page") || "1", 10);
    const qLimit = parseInt(qs.get("limit") || "12", 10);
    const qSort = qs.get("sort") || "relevance";
    setPage(clamp(qPage, 1, 9999));
    setLimit(clamp(qLimit, 6, 60));
    setSort(SORTS.some((s) => s.key === qSort) ? qSort : "relevance");
  }, [routerLocation.search]);

  const fetchListings = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.set("keywords", term);
      params.set("page", String(page));
      params.set("limit", String(limit));
      params.set("sort", sort); // optional; your backend can switch $sort based on this

      const { data } = await axiosInstance.get(`/deepsearch-seller?${params.toString()}`);

      // support both { results } and { data.results }
      const payload = data?.results ? data : data?.data ? data.data : {};
      setResults(payload.results || []);
      setTotal(payload.total || 0);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch search results");
      setResults([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  // fetch when term/page/limit/sort changes
  useEffect(() => {
    fetchListings();
    window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [term, page, limit, sort]);

  const pageCount = Math.ceil(total / limit) || 0;

  const goTo = (p) => {
    const params = new URLSearchParams(routerLocation.search); // ✅ use routerLocation
    params.set("page", String(clamp(p, 1, Math.max(1, pageCount))));
    params.set("limit", String(limit));
    params.set("sort", sort);
    navigate({ pathname: `/seller-list/${encodeURIComponent(term)}`, search: `?${params.toString()}` });
  };

  const changeSort = (next) => {
    const params = new URLSearchParams(routerLocation.search);
    params.set("page", "1");
    params.set("limit", String(limit));
    params.set("sort", next);
    navigate({ pathname: `/seller-list/${encodeURIComponent(term)}`, search: `?${params.toString()}` });
  };

  const changeLimit = (next) => {
    const params = new URLSearchParams(routerLocation.search);
    params.set("page", "1");
    params.set("limit", String(next));
    params.set("sort", sort);
    navigate({ pathname: `/seller-list/${encodeURIComponent(term)}`, search: `?${params.toString()}` });
  };

  return (
    <>
      <Header />

      <main>
        <div className="container py-3">
          {/* Breadcrumb (static placeholders—replace if needed) */}
          <nav aria-label="breadcrumb" className="mb-2">
            <ol className="breadcrumb small mb-0">
              <li className="breadcrumb-item"><a href="#">Delhi</a></li>
              <li className="breadcrumb-item"><a href="#">All Categories</a></li>
              <li className="breadcrumb-item"><a href="#">Products</a></li>
              <li className="breadcrumb-item active" aria-current="page">
                {loading ? "…" : `${total}+`} Listings
              </li>
            </ol>
          </nav>

          {/* Title */}
          <h2 className="fw-bold mb-3 text-capitalize">
            Best {term} Seller
            {total ? <span className="text-muted h6 ms-2">({total} results)</span> : null} 
          </h2>

          {/* Filter bar */}
          <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
            {/* Sort dropdown */}
            <div className="btn-group">
              <button className="btn btn-light border dropdown-toggle" data-bs-toggle="dropdown">
                Sort by: {SORTS.find((s) => s.key === sort)?.label || "Relevance"}
              </button>
              <ul className="dropdown-menu">
                {SORTS.map((s) => (
                  <li key={s.key}>
                    <button
                      className={`dropdown-item ${s.key === sort ? "active" : ""}`}
                      onClick={() => changeSort(s.key)}
                    >
                      {s.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Page size */}
            <div className="btn-group">
              <button className="btn btn-light border dropdown-toggle" data-bs-toggle="dropdown">
                Per page: {limit}
              </button>
              <ul className="dropdown-menu">
                {[12, 24, 36].map((n) => (
                  <li key={n}>
                    <button
                      className={`dropdown-item ${n === limit ? "active" : ""}`}
                      onClick={() => changeLimit(n)}
                    >
                      {n}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Example quick filters (dummies; wire to backend if needed) */}
            <button className="btn btn-light border">Available Now</button>
            <button className="btn btn-light border d-inline-flex align-items-center">
              <span className="me-1">⭐</span> Top Rated
            </button>
            <button className="btn btn-outline-dark d-inline-flex align-items-center">
              <span className="me-2">≡</span> All Filters
            </button>
          </div>

          <div className="row">
            {/* LEFT: results */}
            <div className="col-md-8">
              {/* Loading skeletons */}
              {loading && (
                <div className="row g-3">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div className="col-12" key={i}>
                      <div className="border p-3 rounded">
                        <div className="d-flex gap-3">
                          <div className="skeleton col-3 h-100" style={{ aspectRatio: "1/1" }} />
                          <div className="flex-grow-1">
                            <div className="skeleton mb-2" style={{ height: 24, width: "60%" }} />
                            <div className="skeleton mb-2" style={{ height: 20, width: "30%" }} />
                            <div className="skeleton mb-2" style={{ height: 18, width: "80%" }} />
                            <div className="skeleton mb-2" style={{ height: 18, width: "50%" }} />
                            <div className="d-flex gap-2 mt-3">
                              <div className="skeleton" style={{ height: 36, width: 120 }} />
                              <div className="skeleton" style={{ height: 36, width: 120 }} />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Empty state */}
              {!loading && (!results || results.length === 0) && (
                <div className="border p-4 rounded-4 text-center bg-light">
                  <h5 className="mb-2">No results for “{term}”</h5>
                  <p className="text-muted mb-3">
                    Try different keywords or reduce filters. Products must match at least one word in the title.
                  </p>
                  <button className="btn btn-outline-primary" onClick={() => goTo(1)}>
                    Reset to page 1
                  </button>
                </div>
              )}

              {/* Results */}
              {!loading && results?.length > 0 && (
                <div className="row gy-3">
                  {results.map((item) => (
                    <div className="col-12" key={item._id}>
                      <div className="card border rounded-4 overflow-hidden">
                        <div className="card-body p-2">
                          <div className="d-flex gap-3">
                            {/* image */}
                            <div className="col-3">
                              <Link to={`/seller/${item.slug || item._id}`}>
                                <img
                                  src={ item.gallery?.[0] || "/assets/img/landscape.png"}
                                  alt={item.title}
                                  className="w-100 rounded"
                                  style={{ aspectRatio: "1/1", objectFit: "cover" }}
                                  onError={(e) => {
                                    e.currentTarget.src = "/assets/img/landscape.png";
                                  }}
                                />
                              </Link>
                            </div>

                            {/* content */}
                            <div className="flex-grow-1">

                          
                              <h4 className="h4 fw-medium mt-1 mb-1">
                                <Link to={`/seller/${item.slug || item._id}`} className="text-decoration-none">
                                  {highlight(item.username, tokens)}
                                </Link>
                              </h4>

                            

                              {/* location / user */}
                              <div className="text-muted small mb-2">
                                {item?.statename ? (
                                  <>
                                    <i className="ri-map-pin-line" /> {item.city},{item.statename}
                                  </>
                                ) : (
                                  <span>&nbsp;</span>
                                )}
                              </div>

                               
                             
                           {/* <p className="mb-2 text-white mb-2" style={{ lineHeight: 1.35 }}>
                            <span class="badge bg-primary fw-light">Primary</span>

                                </p> */}
                         
                              {/* {item.description && (
                                <p className="mb-2 text-secondary" style={{ lineHeight: 1.35 }}>
                                  {highlight(
                                    item.description.length > 180
                                      ? item.description.slice(0, 180) + "…"
                                      : item.description,
                                    tokens
                                  )}
                                </p>
                              )} */}

                              {/* actions */}
                              <div className="d-flex gap-2 mt-4 pt-4">
                                <Link to={`/seller/${item.slug || item._id}`} className="btn btn-outline-primary">
                                  View Details
                                </Link>
                                <Link type="button"  to={`/seller/${item.slug || item._id}`}  className="btn btn-success">
                                  <i className="ri-whatsapp-line me-1" />
                                  WhatsApp
                                </Link>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* pagination */}
                  {pageCount > 1 && (
                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <ul className="pagination mb-0">
                        <li className={`page-item ${page <= 1 ? "disabled" : ""}`}>
                          <button className="page-link" type="button" onClick={() => goTo(page - 1)}>
                            <i className="ri-arrow-left-s-line me-1" />
                            Prev
                          </button>
                        </li>
                      </ul>
                      <div className="small text-muted">
                        Page {page} of {pageCount}
                      </div>
                      <ul className="pagination mb-0">
                        <li className={`page-item ${page >= pageCount ? "disabled" : ""}`}>
                          <button className="page-link" type="button" onClick={() => goTo(page + 1)}>
                            Next <i className="ms-1 ri-arrow-right-s-line" />
                          </button>
                        </li>
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* RIGHT: reserved for filters/ads */}
            <div className="col-md-4" />
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
};

export default SearchVendorListing;
